import {
  ActionTypes as PreferenceEditActionTypes,
  IUpdatePreferencesSuccessAction,
} from '@app/components/PreferencesModal/modules/preferencesEdit'
import { PreferenceKeys } from '@app/models/enums'
import { IUser, User } from '@app/models/User'
import { UserPermissions } from '@app/models/UserPermissions'
import { ApplicationState } from '@app/store'
import { actionCreators as GlobalActionCreators } from '@app/store/modules/global'
import * as Cookies from '@app/utils/cookies'
import * as LocalStorage from '@app/utils/localStorage'
import { errorToast } from '@app/utils/notifications'
import {
  makeAuthenticatedRequest,
  makeRequest,
  RequestMethod,
} from '@app/utils/request'
import { fromJS, Map, Record } from 'immutable'
import * as LogRocket from 'logrocket'
import {
  push,
} from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

export enum ActionTypes {
  ClearLoggedInUser = 'ACCOUNT::CLEAR_LOGGED_IN_USER',
  ImpersonateUser = 'ACCOUNT::IMPERSONATE_USER',
  InitializeAccount = 'ACCOUNT::INITIALIZE_ACCOUNT',
  InitializeAccountComplete = 'ACCOUNT::INITIALIZE_ACCOUNT_COMPLETE',
  SetLastPage = 'ACCOUNT::SET_LAST_PAGE',
  SetLoggedInUser = 'ACCOUNT::SET_LOGGED_IN_USER',
}

// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface ICurrentUserState {
  readonly user?: IUser
}

export interface IAccessTokenState {
  readonly accessToken?: string
}

export interface IAccountState extends ICurrentUserState {
  readonly accessToken?: string
  readonly isInitialized: boolean
  readonly preferences: Map<string, any>
}

const AccountStateRecord = Record({
  accessToken: '',
  isInitialized: false,
  preferences: Map<string, any>(),
  user: undefined,
})

export class AccountState extends AccountStateRecord implements IAccountState {
  public static fromJS(initialState?: Partial<IAccountState>) {
    if (!initialState) {
      return new AccountState()
    }

    const { user, preferences, ...rest } = initialState
    return new AccountState({
      preferences: preferences ? fromJS(preferences) : Map<string, any>(),
      user: user ? User.fromJS(user) : undefined,
      ...rest,
    })
  }

  public readonly accessToken?: string
  public readonly isInitialized: boolean
  public readonly preferences: Map<string, any>
  public readonly user?: User

  constructor(props?: Partial<IAccountState>) {
    props ? super(props) : super()
  }
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
export interface IClearLoggedInUserAction {
  type: ActionTypes.ClearLoggedInUser
}

interface IImpersonateUserAction {
  type: ActionTypes.ImpersonateUser
  userId: string
}

interface IInitializeAccountAction {
  type: ActionTypes.InitializeAccount
}

interface IInitializeAccountCompleteAction {
  type: ActionTypes.InitializeAccountComplete
}

interface ISetLastPageAction {
  lastPage: string
  type: ActionTypes.SetLastPage
}

export interface ISetLoggedInUserAction {
  accessToken: string
  preferences: Map<string, any>
  staySignedIn: boolean
  type: ActionTypes.SetLoggedInUser
  user: User
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IClearLoggedInUserAction
  | IInitializeAccountCompleteAction
  | ISetLastPageAction
  | ISetLoggedInUserAction
  | IUpdatePreferencesSuccessAction

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

function clearLoggedInUser() {
  return { type: ActionTypes.ClearLoggedInUser }
}

function impersonateUser(userId: string): IImpersonateUserAction {
  return {
    type: ActionTypes.ImpersonateUser,
    userId,
  }
}

function initializeAccount(): IInitializeAccountAction {
  return { type: ActionTypes.InitializeAccount }
}

function initializeAccountComplete(): IInitializeAccountCompleteAction {
  return { type: ActionTypes.InitializeAccountComplete }
}

function setLastPage(lastPage: string): ISetLastPageAction {
  return {
    lastPage,
    type: ActionTypes.SetLastPage,
  }
}

function setLoggedInUser(
  accessToken: string,
  user: User,
  staySignedIn: boolean,
  preferences: Map<string, any>,
): ISetLoggedInUserAction {
  return {
    accessToken,
    preferences,
    staySignedIn,
    type: ActionTypes.SetLoggedInUser,
    user,
  }
}

export const actionCreators = {
  clearLoggedInUser,
  initializeAccount,
  setLoggedInUser,
}

export const impersonateActionCreators = {
  impersonateUser,
}

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const reducer: Reducer<AccountState> = (
  state: AccountState = new AccountState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction

  switch (action.type) {
    case ActionTypes.ClearLoggedInUser:
      return new AccountState()

    case ActionTypes.InitializeAccountComplete:
      return state.set('isInitialized', true) as AccountState

    case ActionTypes.SetLastPage:
      return state.setIn(
        ['preferences', PreferenceKeys.LastPage],
        action.lastPage,
      ) as AccountState

    case ActionTypes.SetLoggedInUser:
      return state
        .set('accessToken', action.accessToken)
        .set('isInitialized', true)
        .set('preferences', action.preferences)
        .set('user', action.user) as AccountState

    case PreferenceEditActionTypes.UpdatePreferencesSuccess:
      return state.setIn(
        ['preferences', PreferenceKeys.LandingPage],
        action.landingPage,
      ) as AccountState

    default:
      const exhaustiveCheck: never = action
  }

  return state
}

export function createPreferencesFromResponse(result: any): Map<string, any> {
  if (
    !result.user ||
    !result.user.user ||
    !Array.isArray(result.user.user.preferences)
  ) {
    return Map<string, any>()
  }

  const preferences = Map<string, any>().withMutations((prefs) => {
    result.user.user.preferences.forEach((p: any) => {
      prefs.set(p.Key, p.Value)
    })
  })

  return preferences
}

export function createUserFromResponse(result: any): User | undefined {
  const account = result.user

  if (account && account.user) {
    const permissions = new UserPermissions({
      accessAllPipelines:
        account.user.permissions.BrokeragePipeline_AccessAllPipelines,
      editFeeDistribution:
        account.user.permissions.BrokeragePipeline_EditFeeDistribution,
      isAdmin: account.user.isAdmin,
      isBrokerageUser: account.user.isBrokerageUser,
      isDevelopmentUser: account.user.isDevelopmentUser,
      isGuest: account.user.isGuest,
      setFeaturedListing: account.user.permissions.Listing_SetFeaturedListing,
    })

    const user = new User({
      firstName: account.firstName,
      id: account.id,
      lastName: account.lastName,
      permissions,
      status: account.user.status,
      title: account.title,
      username: account.user.username,
    })

    return user
  } else {
    return undefined
  }
}

// ----------------
// SAGAS - Asyncronous Saga action
function* clearLoggedInUserSaga(action: IClearLoggedInUserAction) {
  try {
    yield LocalStorage.deleteItem(LocalStorage.StorageKey.StaySignedIn)
    Cookies.deleteItem(Cookies.CookieKey.AccountState)

    yield put(push('/login'))
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* impersonateUserSaga(action: IImpersonateUserAction) {
  try {
    const currentUser: User | undefined = yield select(selectCurrentUser())

    if (
      !currentUser ||
      !currentUser.permissions.isAdmin ||
      currentUser.status !== 'System'
    ) {
      return
    }

    const result = yield makeAuthenticatedRequest(
      `/api/Users/Impersonate/${action.userId}`,
      { method: RequestMethod.POST },
    )

    if (result.success === true) {
      const user = createUserFromResponse(result)
      const preferences = createPreferencesFromResponse(result)

      if (user) {
        yield put(setLoggedInUser(result.accessToken, user, false, preferences))
        yield put(push('/'))
      }
    }
  } catch (err) {
    console.error(err)
  }
}

function* initializeAccountSaga() {
  try {
    const staySignedIn: boolean = yield LocalStorage.getItem<boolean>(
      LocalStorage.StorageKey.StaySignedIn,
    )

    const accountStateString =
      staySignedIn === true
        ? yield LocalStorage.getItem<string>(
            LocalStorage.StorageKey.AccountState,
          )
        : Cookies.getItem(Cookies.CookieKey.AccountState)

    if (accountStateString) {
      const accountState = JSON.parse(atob(accountStateString))

      if (accountState && accountState.accessToken) {
        const reqOpts = {
          body: JSON.stringify(accountState),
          method: RequestMethod.POST,
        }

        const result = yield makeRequest(
          '/api/Users/ValidateAccessToken',
          reqOpts,
        )

        if (result.success === true) {
          const user = createUserFromResponse(result)
          const preferences = createPreferencesFromResponse(result)

          if (user) {
            yield put(
              setLoggedInUser(
                result.accessToken,
                user,
                staySignedIn,
                preferences,
              ),
            )
          }
        } else {
        }
      }
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  } finally {
    yield put(initializeAccountComplete())
  }
}

function* setLoggedInUserSaga(action: ISetLoggedInUserAction) {
  try {
    LogRocket.identify(action.user.id, {
      email: action.user.username,
      name: `${action.user.firstName} ${action.user.lastName}`,
    })

    const accountState: AccountState = yield select(selectModuleState())
    const accountStateString = btoa(
      JSON.stringify({
        accessToken: accountState.accessToken,
      }),
    )

    yield LocalStorage.setItem(
      LocalStorage.StorageKey.StaySignedIn,
      action.staySignedIn,
    )

    if (action.staySignedIn) {
      yield LocalStorage.setItem(
        LocalStorage.StorageKey.AccountState,
        accountStateString,
      )
    } else {
      Cookies.setItem(Cookies.CookieKey.AccountState, accountStateString)
    }
    yield put(GlobalActionCreators.initializeGlobal(action.user))
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchClearLoggedInUser() {
  yield takeLatest(ActionTypes.ClearLoggedInUser, clearLoggedInUserSaga)
}

function* watchImpersonateUser() {
  yield takeLatest(ActionTypes.ImpersonateUser, impersonateUserSaga)
}

function* watchInitializeAccount() {
  yield takeLatest(ActionTypes.InitializeAccount, initializeAccountSaga)
}

function* watchSetLoggedInUser() {
  yield takeLatest(ActionTypes.SetLoggedInUser, setLoggedInUserSaga)
}

export function* accountRootSaga() {
  yield fork(watchClearLoggedInUser)
  yield fork(watchImpersonateUser)
  yield fork(watchInitializeAccount)
  yield fork(watchSetLoggedInUser)
}

// ----------------
// SELECTORS - Asyncronous Saga action
const selectModuleState = () => (state: ApplicationState) => state.account

export const selectAccessToken = () =>
  createSelector(
    selectModuleState(),
    (accountState) => accountState.accessToken,
  )

export const selectCurrentUser = () =>
  createSelector(selectModuleState(), (accountState) => accountState.user)

export const selectIsInitialized = () =>
  createSelector(
    selectModuleState(),
    (accountState) => accountState.isInitialized,
  )

export const selectPreferences = () =>
  createSelector(
    selectModuleState(),
    (accountState) => accountState.preferences,
  )

export const selectPreference = (key: string) =>
  createSelector(selectPreferences(), (preferences) => preferences.get(key))
